import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Avatar,
  Chip,
  Stack,
  useTheme,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  SwapHoriz as SwapHorizIcon,
  SmartToy as SmartToyIcon,
  Analytics as AnalyticsIcon,
  Place as PositionIcon,
  ContentCopy as ContentCopyIcon,
  RestartAlt as RestartAltIcon,
  Home as HomeIcon,
  Handshake as HandshakeIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  CameraAlt as CameraAltIcon,
  History as HistoryIcon,
  EmojiEmotions as EmojiEmotionsIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  Flag as FlagIcon,
  Dashboard as DashboardIcon,
  AccountCircle as AccountIcon,
  EmojiObjects as EmojiObjectsIcon,
  VolumeUp as VolumeUpIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';

const EnhancedChessDrawer = ({
  open,
  onClose,
  responsive,
  t,
  gameState,
  dispatch,
  game,
  togglePlayerColor,
  sendToGameAnalysis,
  shouldShowAnalytics,
  applyCustomFen,
  handleResetGame,
  navigate,
  // ControlPanel props
  playerColorFromServer,
  gameIdFromServer,
  onCaptureBoard,
  setIsViewer,
  gameOver,
  onAccordionChange,
  onGameHistoryChange,
  getMoveHistory,
  aiLevel,
  gameInfo,
  winnerColor,
  learnMode,
  evaluation,
  showGameHistory,
  userData,
  topSubscription,
  generalMessageViaState,
  location,
  sendMessage,
  generalMessageTemplate,
  setShowHistoryLimitDialog,
  GENERAL,
  // Button visibility configuration
  buttonConfig = {
    // Primary Actions
    switchColor: true,
    aiSettings: true,
    
    // Game Actions
    offerDraw: true,
    saveGame: true,
    inviteViewer: true,
    shareScreen: true,
    showHistory: true,
    sendSmile: true,
    
    // Analysis
    gameAnalysis: true,
    
    // Position Controls
    setPosition: true,
    copyPgn: true,
    copyFen: true,
    
    // System
    gameInfo: true,
    settings: true,
    account: true,
    
    // Actions
    restartGame: true,
    surrender: true,
    mainMenu: true,
    
    // Sections
    showPrimarySection: true,
    showGameActionsSection: true,
    showAnalysisSection: true,
    showPositionSection: true,
    showSystemSection: true,
    showActionsSection: true,
    
    // Blind Chess Features
    bestMove: true,
    speechSynthesis: true,
    autoAiMove: true,
    hiddenMode: true,
    sayLastMove: true,
    
    // Blind Chess Section
    showBlindSection: true,
  },
  // Drawer behavior options
  drawerOptions = {
    keepBoardVisible: false,
    overlayMode: false,
    backdropBlur: true,
    autoClose: true,
    allowDragDrop: true,
    initialPosition: 'right',
  },
  // Blind chess props
  speechEnabled = false,
  setSpeechEnabled = () => {},
  enableSpeech = () => {},
  disableSpeech = () => {},
  autoOpponentMove = false,
  setAutoOpponentMove = () => {},
  runAiMove = () => {},
  speakText = () => {},
  hiddenMode = false,
  setHiddenMode = () => {},
  lastMove = null,
  handleSayLastMove = () => {},
  invalidCount = { w: 0, b: 0 },
  isSpeaking = false,
  // Dialog handler functions from Clock component
  onOfferDraw = () => {},
  onSaveGame = () => {},
  onSendEmoji = () => {},
  onShowGameInfo = () => {},
  onShowAccount = () => {},
  onShowSettings = () => {},
  onSurrender = () => {},
  onInviteViewer = () => {},
}) => {
  const theme = useTheme();
  
  // Local state
  const [showHistoryText, setShowHistoryText] = useState(t("Show History"));
  const [viewer, setViewer] = useState(false);
  
  // Drawer position and drag state
  const [drawerPosition, setDrawerPosition] = useState(drawerOptions.initialPosition || 'right');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartX, setDragStartX] = useState(0);
  const drawerRef = useRef(null);
  
  // Player state
  const [playerColor, setPlayerColor] = useState(null);
  const [gameId, setGameId] = useState(null);

  useEffect(() => {
    if (playerColorFromServer != null) {
      setPlayerColor(playerColorFromServer);
    }
  }, [playerColorFromServer]);

  useEffect(() => {
    if (gameIdFromServer != null) {
      setGameId(gameIdFromServer);
    }
  }, [gameIdFromServer]);

  useEffect(() => {
    if (showGameHistory === true) {
      setShowHistoryText(t("Hide History"));
    } else {
      setShowHistoryText(t("Show History"));
    }
  }, [showGameHistory, t]);

  useEffect(() => {
    if (setIsViewer) {
      setViewer(setIsViewer);
    }
  }, [setIsViewer]);

  // Reset drawer position when drawer options change
  useEffect(() => {
    if (drawerOptions.initialPosition && drawerOptions.initialPosition !== drawerPosition) {
      setDrawerPosition(drawerOptions.initialPosition);
    }
  }, [drawerOptions.initialPosition, drawerPosition]);

  // Drag and drop handlers
  const handleDragStart = useCallback((e) => {
    if (!drawerOptions.allowDragDrop) return;
    
    setIsDragging(true);
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    setDragStartY(clientY);
    setDragStartX(clientX);
    
    e.preventDefault();
    e.stopPropagation();
  }, [drawerOptions.allowDragDrop]);

  const handleDragMove = useCallback((e) => {
    if (!isDragging || !drawerOptions.allowDragDrop) return;
    
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    
    const deltaY = clientY - dragStartY;
    const deltaX = clientX - dragStartX;
    
    const dragThreshold = 50;
    
    if (Math.abs(deltaY) > dragThreshold || Math.abs(deltaX) > dragThreshold) {
      if (drawerPosition === 'right') {
        if (deltaY > dragThreshold && Math.abs(deltaY) > Math.abs(deltaX)) {
          setDrawerPosition('bottom');
          setIsDragging(false);
        }
      } else if (drawerPosition === 'bottom') {
        if (deltaX > dragThreshold && Math.abs(deltaX) > Math.abs(deltaY)) {
          setDrawerPosition('right');
          setIsDragging(false);
        }
      }
    }
    
    e.preventDefault();
    e.stopPropagation();
  }, [isDragging, dragStartY, dragStartX, drawerPosition, drawerOptions.allowDragDrop]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDragStartY(0);
    setDragStartX(0);
  }, []);

  // Add event listeners for drag functionality
  useEffect(() => {
    if (!drawerOptions.allowDragDrop) return;
    
    const handleMouseMove = (e) => handleDragMove(e);
    const handleMouseUp = () => handleDragEnd();
    const handleTouchMove = (e) => handleDragMove(e);
    const handleTouchEnd = () => handleDragEnd();
    
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd, drawerOptions.allowDragDrop]);

  // Mobile auto-close helper function
  const handleMobileAutoClose = useCallback(() => {
    if (responsive.isMobile && drawerOptions.autoClose) {
      onClose();
    }
  }, [responsive.isMobile, drawerOptions.autoClose, onClose]);

  // Close drawer wrapper
  const handleDrawerButtonClick = useCallback((action) => {
    return (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      if (action) {
        action();
      }
    };
  }, []);

  const handleShowHistory = () => {
    const newHistoryState = !showGameHistory;
    if (onGameHistoryChange) {
      onGameHistoryChange(newHistoryState);
    }
  };

  const handleCaptureBoard = () => {
    if (onCaptureBoard) {
      onCaptureBoard();
    }
    if (drawerOptions.autoClose) {
      onClose();
    }
  };

  const getLastMoveText = () => {
    if (!lastMove) return t("sayLastMove");
    return `${lastMove.from}-${lastMove.to}`;
  };

  // Get drawer styles based on position
  const getDrawerStyles = () => {
    const baseStyles = {
      zIndex: 9999,
      "& .MuiDrawer-paper": {
        background: theme.palette.background.paper,
        boxShadow: `0 10px 30px rgba(0, 0, 0, 0.15)`,
        position: drawerOptions.keepBoardVisible ? 'fixed' : 'relative',
        cursor: drawerOptions.allowDragDrop ? 'grab' : 'default',
        userSelect: 'none',
        transition: isDragging ? 'none' : 'all 0.3s ease-in-out',
      },
      "& .MuiBackdrop-root": {
        backgroundColor: drawerOptions.keepBoardVisible ? "transparent" : "rgba(0, 0, 0, 0.5)",
        backdropFilter: "none",
        display: drawerOptions.keepBoardVisible ? "none" : "block",
      },
    };

    if (drawerPosition === 'bottom') {
      baseStyles["& .MuiDrawer-paper"] = {
        ...baseStyles["& .MuiDrawer-paper"],
        width: '100%',
        maxWidth: '100%',
        height: responsive.isMobile ? '100vh' : '50vh',
        maxHeight: responsive.isMobile ? '100vh' : '50vh',
        borderRadius: '20px 20px 0 0',
        bottom: 0,
        top: 'auto',
      };
    } else {
      baseStyles["& .MuiDrawer-paper"] = {
        ...baseStyles["& .MuiDrawer-paper"],
        width: responsive.isMobile ? "95vw" : 400,
        maxWidth: responsive.isMobile ? "400px" : "400px",
        height: drawerOptions.keepBoardVisible ? '100vh' : 'auto',
        borderRadius: responsive.isMobile ? '0' : '20px 0 0 20px',
        top: drawerOptions.keepBoardVisible ? 0 : 'auto',
      };
    }

    return baseStyles;
  };

  // Enhanced button configuration with Clock component dialog handlers
  const primaryButtons = [
    {
      label: t("Switch color"),
      icon: <SwapHorizIcon />,
      action: handleDrawerButtonClick(() => {
        togglePlayerColor();
        handleMobileAutoClose();
      }),
      color: 'primary',
      variant: 'contained',
      disabled: false,
      visible: buttonConfig.switchColor,
    },
    {
      label: `${t("AI_skill")}: ${gameState.autoAIMoves ? t("On") : t("Off")}. ${t("Level")}: ${aiLevel}`,
      icon: <SmartToyIcon />,
      action: handleDrawerButtonClick(() => {
        dispatch({ type: "TOGGLE_AI_DIALOG", payload: true });
        handleMobileAutoClose();
      }),
      color: 'secondary',
      variant: 'contained',
      disabled: false,
      visible: buttonConfig.aiSettings,
    },
  ].filter(button => button.visible);

  // Blind chess specific buttons
  const blindButtons = [
    {
      label: t("Best Move"),
      icon: <EmojiObjectsIcon />,
      action: handleDrawerButtonClick(() => {
        const currentTurn = game.turn();
        const isOpponentTurn = currentTurn !== playerColor;

        if (isOpponentTurn) {
          runAiMove();
        } else {
          speakText(t("It's your turn to move"));
        }
        handleMobileAutoClose();
      }),
      color: 'success',
      variant: 'contained',
      disabled: false,
      visible: buttonConfig.bestMove,
    },
    {
      label: getLastMoveText(),
      icon: <VolumeUpIcon />,
      action: handleDrawerButtonClick(() => {
        handleSayLastMove();
        handleMobileAutoClose();
      }),
      color: lastMove ? 'primary' : '#666666',
      variant: 'contained',
      disabled: isSpeaking,
      visible: buttonConfig.sayLastMove,
    },
    {
      label: hiddenMode ? t("showBoard") : t("hideBoard"),
      icon: hiddenMode ? <VisibilityIcon /> : <VisibilityOffIcon />,
      action: handleDrawerButtonClick(() => {
        setHiddenMode(!hiddenMode);
        handleMobileAutoClose();
      }),
      color: 'warning',
      variant: 'contained',
      disabled: false,
      visible: buttonConfig.hiddenMode,
    },
  ].filter(button => button.visible);

  const gameButtons = [
    {
      label: t("Offer Draw"),
      icon: <HandshakeIcon />,
      action: handleDrawerButtonClick(() => {
        onClose(); // Close drawer first
        setTimeout(() => {
          onOfferDraw(); // Use Clock component's handler
        }, 150);
      }),
      color: '#00796b',
      disabled: learnMode || gameOver,
      visible: buttonConfig.offerDraw && !learnMode,
    },
    {
      label: t("Save Game"),
      icon: <SaveIcon />,
      action: handleDrawerButtonClick(() => {
        onClose(); // Close drawer first
        setTimeout(() => {
          onSaveGame(); // Use Clock component's handler
        }, 150);
      }),
      color: '#0288d1',
      disabled: !gameOver,
      visible: buttonConfig.saveGame,
    },
    {
      label: t("Invite Viewer"),
      icon: <VisibilityIcon />,
      action: handleDrawerButtonClick(() => {
        onClose(); // Close drawer first
        setTimeout(() => {
          onInviteViewer(); // Use Clock component's handler
        }, 150);
      }),
      color: '#ffa726',
      disabled: learnMode || gameOver,
      visible: buttonConfig.inviteViewer && !learnMode,
    },
    {
      label: t("Share Screen"),
      icon: <CameraAltIcon />,
      action: handleDrawerButtonClick(() => {
        handleCaptureBoard();
        // Note: handleCaptureBoard already has auto-close logic
      }),
      color: '#388e3c',
      disabled: false,
      visible: buttonConfig.shareScreen,
    },
    {
      label: showHistoryText,
      icon: <HistoryIcon />,
      action: handleDrawerButtonClick(() => {
        handleShowHistory();
        handleMobileAutoClose();
      }),
      color: '#7b5c91',
      disabled: false,
      visible: buttonConfig.showHistory,
    },
    {
      label: t("Send Smile"),
      icon: <EmojiEmotionsIcon />,
      action: handleDrawerButtonClick(() => {
        onClose(); // Close drawer first
        setTimeout(() => {
          onSendEmoji(); // Use Clock component's handler
        }, 150);
      }),
      color: '#ffeb3b',
      textColor: '#000',
      disabled: learnMode || gameOver,
      visible: buttonConfig.sendSmile && !learnMode,
    },
  ].filter(button => button.visible);

  const analysisButtons = (shouldShowAnalytics() && buttonConfig.gameAnalysis) ? [
    {
      label: t("Game Analysis"),
      icon: <AnalyticsIcon />,
      action: handleDrawerButtonClick(() => {
        sendToGameAnalysis();
        handleMobileAutoClose();
      }),
      color: '#e91e63',
      variant: 'contained',
      disabled: false,
      visible: true,
    },
  ] : [];

  const positionButtons = [
    {
      label: t("Set Position"),
      icon: <PositionIcon />,
      action: handleDrawerButtonClick(() => {
        dispatch({ type: "TOGGLE_FEN_DIALOG", payload: true });
        handleMobileAutoClose();
      }),
      color: '#9c27b0',
      disabled: false,
      visible: buttonConfig.setPosition,
    },
    {
      label: t("Copy PGN"),
      icon: <ContentCopyIcon />,
      action: handleDrawerButtonClick(() => {
        const pgn = game.pgn();
        navigator.clipboard.writeText(pgn);
        Swal.fire({
          icon: "success",
          title: t("PGN copied to clipboard"),
        });
        handleMobileAutoClose();
      }),
      color: '#795548',
      disabled: false,
      visible: buttonConfig.copyPgn,
    },
    {
      label: t("Copy FEN"),
      icon: <ContentCopyIcon />,
      action: handleDrawerButtonClick(() => {
        const fen = game.fen();
        navigator.clipboard.writeText(fen);
        Swal.fire({
          icon: "success",
          title: t("FEN copied to clipboard"),
        });
        handleMobileAutoClose();
      }),
      color: '#607d8b',
      disabled: false,
      visible: buttonConfig.copyFen,
    },
  ].filter(button => button.visible);

  const systemButtons = [
    {
      label: t("Game Info"),
      icon: <InfoIcon />,
      action: handleDrawerButtonClick(() => {
        onClose(); // Close drawer first
        setTimeout(() => {
          onShowGameInfo(); // Use Clock component's handler
        }, 150);
      }),
      color: '#2196f3',
      disabled: false,
      visible: buttonConfig.gameInfo,
    },
    {
      label: t("Show Settings"),
      icon: <SettingsIcon />,
      action: handleDrawerButtonClick(() => {
        onClose(); // Close drawer first
        setTimeout(() => {
          onShowSettings(); // Use Clock component's handler
        }, 150);
      }),
      color: '#6a1b9a',
      disabled: false,
      visible: buttonConfig.settings,
    },
    {
      label: t("Show Account"),
      icon: <AccountIcon />,
      action: handleDrawerButtonClick(() => {
        onClose(); // Close drawer first
        setTimeout(() => {
          onShowAccount(); // Use Clock component's handler
        }, 150);
      }),
      color: '#1e88e5',
      disabled: false,
      visible: buttonConfig.account,
    },
  ].filter(button => button.visible);

  const actionButtons = [
    {
      label: t("Restart Game"),
      icon: <RestartAltIcon />,
      action: handleDrawerButtonClick(() => {
        handleResetGame();
        handleMobileAutoClose();
      }),
      color: 'error',
      variant: 'contained',
      disabled: false,
      visible: buttonConfig.restartGame,
    },
    {
      label: t("Surrender"),
      icon: <FlagIcon />,
      action: handleDrawerButtonClick(() => {
        onClose(); // Close drawer first
        setTimeout(() => {
          onSurrender(); // Use Clock component's handler
        }, 150);
      }),
      color: '#d32f2f',
      disabled: learnMode || gameOver,
      visible: buttonConfig.surrender && !learnMode,
    },
    {
      label: t("Main"),
      icon: <HomeIcon />,
      action: handleDrawerButtonClick(() => {
        navigate("/main");
        handleMobileAutoClose();
      }),
      color: 'success',
      variant: 'contained',
      disabled: false,
      visible: buttonConfig.mainMenu,
    },
  ].filter(button => button.visible);

  const renderButton = (button, key) => {
    if (!button.visible) return null;
    
    const isCustomColor = button.color && button.color.startsWith('#');
    const muiColor = isCustomColor ? 'primary' : button.color;
    
    return (
      <Button
        key={key}
        variant={button.variant || 'outlined'}
        color={muiColor}
        startIcon={button.icon}
        onClick={button.action}
        disabled={button.disabled}
        fullWidth
        sx={{
          height: responsive.isMobile ? '38px' : '40px',
          fontSize: responsive.isMobile ? '0.75rem' : '0.8rem',
          fontWeight: 500,
          textTransform: 'none',
          borderRadius: '8px',
          boxShadow: button.variant === 'contained' ? '0 2px 6px rgba(0,0,0,0.1)' : 'none',
          transition: 'all 0.15s ease-in-out',
          backgroundColor: (button.variant === 'outlined' && isCustomColor) ? button.color : undefined,
          color: button.textColor || (button.variant === 'outlined' && isCustomColor ? 'white' : undefined),
          border: button.variant === 'outlined' ? 'none' : undefined,
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 3px 8px rgba(0,0,0,0.15)',
            backgroundColor: (button.variant === 'outlined' && isCustomColor) ? 
              button.color + 'dd' : undefined,
          },
          '&:disabled': {
            opacity: 0.5,
            transform: 'none',
          },
        }}
      >
        {button.label}
      </Button>
    );
  };

  const renderSection = (title, buttons, icon, sectionVisible = true) => {
    if (!sectionVisible || buttons.length === 0) return null;
    
    return (
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {icon}
          <Typography 
            variant="h6" 
            sx={{ 
              ml: 1,
              fontSize: responsive.isMobile ? '0.9rem' : '0.95rem',
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            {title}
          </Typography>
        </Box>
        <Stack spacing={0.5}>
          {buttons.map((button, index) => renderButton(button, `${title}-${index}`))}
        </Stack>
      </Box>
    );
  };

  return (
    <>
      {/* Drawer Component - Only contains the drawer UI, no dialogs */}
      <Drawer
        anchor={drawerPosition}
        open={open}
        onClose={onClose}
        variant={drawerOptions.overlayMode ? "temporary" : "persistent"}
        sx={getDrawerStyles()}
        ModalProps={{
          keepMounted: true,
          disableBackdropClick: drawerOptions.keepBoardVisible,
        }}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            backgroundColor: "white"
          }}
        >
          {/* Drag Handle */}
          {drawerOptions.allowDragDrop && (
            <Box
              className="drag-handle"
              sx={{
                width: '100%',
                height: '24px',
                backgroundColor: theme.palette.primary.main,
                cursor: 'grab',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                '&:active': {
                  cursor: 'grabbing',
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
              onMouseDown={handleDragStart}
              onTouchStart={handleDragStart}
            >
              <Box
                sx={{
                  width: '40px',
                  height: '4px',
                  backgroundColor: theme.palette.primary.contrastText,
                  borderRadius: '2px',
                  position: 'relative',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  right: 8,
                  fontSize: '0.65rem',
                  color: theme.palette.primary.contrastText,
                }}
              >
                {drawerPosition === 'right' ? '↓' : '→'}
              </Typography>
            </Box>
          )}

          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              borderBottom: `5px solid ${theme.palette.divider}`,
              backgroundColor: "white",
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                sx={{
                  bgcolor: theme.palette.primary.main,
                  mr: 1.5,
                  width: 32,
                  height: 32,
                }}
              >
                <DashboardIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: responsive.isMobile ? '1.1rem' : '1.25rem',
                  }}
                >
                  {t("Game Controls")}
                </Typography>
                {playerColor && (
                  <Chip
                    label={t(`Playing as ${playerColor}`)}
                    size="small"
                    color={playerColor === 'white' ? 'default' : 'primary'}
                    sx={{ mt: 0.25, fontSize: '0.7rem', height: 20 }}
                  />
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={onClose}
                sx={{
                  bgcolor: theme.palette.error.main,
                  color: theme.palette.error.contrastText,
                  '&:hover': {
                    bgcolor: theme.palette.error.dark,
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 1.5,
              display: 'flex',
              flexDirection: drawerPosition === 'bottom' ? 'row' : 'column',
              flexWrap: drawerPosition === 'bottom' ? 'wrap' : 'nowrap',
              gap: drawerPosition === 'bottom' ? 1.5 : 0,
              '&::-webkit-scrollbar': {
                width: '6px',
                height: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: theme.palette.grey[100],
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: theme.palette.primary.main,
                borderRadius: '3px',
                '&:hover': {
                  background: theme.palette.primary.dark,
                },
              },
            }}
          >
            {/* Primary Actions */}
            {renderSection(t("Primary Actions"), primaryButtons, <SwapHorizIcon color="primary" />, buttonConfig.showPrimarySection)}
            
            {buttonConfig.showPrimarySection && primaryButtons.length > 0 && (
              <Divider sx={{ my: 1 }} />
            )}
            
            {/* Blind Chess Features */}
            {renderSection(t("Blind Chess"), blindButtons, <VolumeUpIcon color="secondary" />, buttonConfig.showBlindSection)}
            
            {buttonConfig.showBlindSection && blindButtons.length > 0 && (
              <Divider sx={{ my: 1 }} />
            )}
            
            {/* Blind Chess Settings */}
            {buttonConfig.showBlindSection && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SmartToyIcon color="info" />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      ml: 1,
                      fontSize: responsive.isMobile ? '0.9rem' : '0.95rem',
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {t("Blind Settings")}
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  {/* Speech Synthesis Toggle */}
                  {buttonConfig.speechSynthesis && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={speechEnabled}
                          onChange={(e) => {
                            if (e.target.checked) {
                              enableSpeech();
                            } else {
                              disableSpeech();
                            }
                            handleMobileAutoClose();
                          }}
                          name="speechEnabled"
                          color="primary"
                          size={responsive.isMobile ? "small" : "medium"}
                        />
                      }
                      label={t("Speech Synthesis")}
                      sx={{ 
                        fontSize: responsive.isMobile ? "0.75rem" : "0.8rem",
                        '& .MuiFormControlLabel-label': {
                          fontSize: responsive.isMobile ? "0.75rem" : "0.8rem",
                        }
                      }}
                    />
                  )}
                  
                  {/* Auto AI Move Toggle */}
                  {buttonConfig.autoAiMove && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={autoOpponentMove}
                          onChange={(e) => {
                            setAutoOpponentMove(e.target.checked);
                            handleMobileAutoClose();
                          }}
                          name="autoOpponentMove"
                          color="primary"
                          size={responsive.isMobile ? "small" : "medium"}
                        />
                      }
                      label={t("Auto AI Move")}
                      sx={{ 
                        fontSize: responsive.isMobile ? "0.75rem" : "0.8rem",
                        '& .MuiFormControlLabel-label': {
                          fontSize: responsive.isMobile ? "0.75rem" : "0.8rem",
                        }
                      }}
                    />
                  )}
                  
                  {/* AI Level Selector */}
                  {buttonConfig.aiSettings && (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5, fontSize: responsive.isMobile ? "0.75rem" : "0.8rem" }}>
                        {t("AI Level")}
                      </Typography>
                      <Select
                        value={aiLevel}
                        onChange={(e) => {
                          dispatch({ type: "SET_AI_LEVEL", payload: parseInt(e.target.value) });
                          handleMobileAutoClose();
                        }}
                        size="small"
                        fullWidth
                        sx={{ fontSize: responsive.isMobile ? "0.75rem" : "0.8rem" }}
                      >
                        {Array.from({ length: 20 }, (_, i) => (
                          <MenuItem key={i + 1} value={i + 1}>
                            {t("AI Level")} {i + 1}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  )}
                  
                  {/* Invalid Move Counter */}
                  <Box sx={{ 
                    p: 0.5,
                    backgroundColor: theme.palette.grey[100],
                    borderRadius: 1,
                    textAlign: 'center'
                  }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: responsive.isMobile ? "0.65rem" : "0.7rem",
                      }}
                    >
                      {t("Invalid moves")} - {t("White")}: {invalidCount.w}, {t("Black")}: {invalidCount.b}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}
            
            {buttonConfig.showBlindSection && (
              <Divider sx={{ my: 1 }} />
            )}
            
            {/* Analysis */}
            {renderSection(t("Analysis"), analysisButtons, <AnalyticsIcon color="secondary" />, buttonConfig.showAnalysisSection)}
            
            {buttonConfig.showAnalysisSection && analysisButtons.length > 0 && (
              <Divider sx={{ my: 1 }} />
            )}
            
            {/* Game Actions */}
            {renderSection(t("Game Actions"), gameButtons, <SaveIcon color="info" />, buttonConfig.showGameActionsSection)}
            
            {buttonConfig.showGameActionsSection && gameButtons.length > 0 && (
              <Divider sx={{ my: 1 }} />
            )}
            
            {/* Position Controls */}
            {renderSection(t("Position Controls"), positionButtons, <PositionIcon color="warning" />, buttonConfig.showPositionSection)}
            
            {buttonConfig.showPositionSection && positionButtons.length > 0 && (
              <Divider sx={{ my: 1 }} />
            )}
            
            {/* System */}
            {renderSection(t("System"), systemButtons, <SettingsIcon color="info" />, buttonConfig.showSystemSection)}
            
            {buttonConfig.showSystemSection && systemButtons.length > 0 && (
              <Divider sx={{ my: 1 }} />
            )}
            
            {/* Actions */}
            {renderSection(t("Actions"), actionButtons, <FlagIcon color="error" />, buttonConfig.showActionsSection)}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default EnhancedChessDrawer;